import { useState } from "react";
import { Redirect, useLocation } from "wouter";
import { Loader2, MapPin, Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useSwitchUnit } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { BrandTile } from "@/components/BrandTile";
import { cn } from "@/lib/utils";

// Marca de "já passou pelo seletor nesta carga da página". Em MEMÓRIA de
// propósito: ela morre no refresh, que é o que faz atualizar a raiz cair no
// seletor de novo (pedido do Pedro em 01/08/2026). Já foi `sessionStorage`, que
// sobrevive ao reload e por isso mostrava o seletor uma única vez por aba.
// Continua valendo para navegação client-side: escolher a loja marca aqui e o
// `setLocation("/")` seguinte não volta pro seletor — sem isso vira laço.
let jaEscolheuNestaCarga = false;

export function marcarEscolhaDeLoja(): void {
  jaEscolheuNestaCarga = true;
}

export function jaEscolheuLoja(): boolean {
  return jaEscolheuNestaCarga;
}

export default function Lojas() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [entrando, setEntrando] = useState<string | null>(null);

  interface LojaDoSeletor {
    id: string;
    name: string;
    address?: string | null;
    logoUrl?: string | null;
    active?: boolean;
  }
  const units: LojaDoSeletor[] = user?.units || [];
  const lojaAtual = user?.store?.id;

  const switchMutation = useSwitchUnit({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries();
        setLocation("/");
      },
      onError: () => setEntrando(null),
    },
  });

  // Quem tem uma loja só nunca vê esta tela — entra direto.
  if (units.length <= 1) {
    marcarEscolhaDeLoja();
    return <Redirect to="/" />;
  }

  const entrar = (id: string) => {
    if (entrando) return;
    marcarEscolhaDeLoja();
    setEntrando(id);
    if (id === lojaAtual) {
      setLocation("/");
      return;
    }
    switchMutation.mutate({ data: { unitId: id } });
  };

  return (
    <div className="min-h-[100svh] w-full flex flex-col items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <BrandTile size="md" className="shadow-md" />
          <h1 className="text-2xl font-bold font-display">Escolha a loja</h1>
        </div>

        <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
          {units.map((unit, i) => (
            <button
              key={unit.id}
              onClick={() => entrar(unit.id)}
              disabled={!!entrando}
              className={cn(
                "w-full text-left px-4 py-4 flex items-center gap-3 transition-colors cursor-pointer",
                "hover:bg-muted/50 disabled:opacity-60 disabled:cursor-default",
                i > 0 && "border-t border-border/50",
              )}
            >
              {unit.logoUrl ? (
                <img src={unit.logoUrl} alt="" className="w-10 h-10 rounded-xl object-cover shrink-0" />
              ) : (
                <BrandTile size="sm" className="w-10 h-10 rounded-xl" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={cn("font-semibold truncate", unit.active === false && "text-muted-foreground")}>
                    {unit.name}
                  </span>
                  {unit.active === false && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium shrink-0">
                      Desativada
                    </span>
                  )}
                </div>
                {unit.address && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-xs text-muted-foreground truncate">{unit.address}</span>
                  </div>
                )}
              </div>
              {entrando === unit.id ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
              ) : unit.id === lojaAtual ? (
                <Check className="w-4 h-4 text-accent shrink-0" />
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
